/**
 * selectors
 */
const SELECTORS = {
    page: '.js-page',
    adserver: '.cc-adserver',
    dataPageFilter: '[data-page-filter]'
};
/**
 * sizes for lp (only needed on breakpoint larger medium)
 */
const size = {
    sm: 5 / 6,
    md: 1 / 3
};
const widthContainer = document.querySelector('.cc-container-full')?.clientWidth ?? 0;

// TODO, can be removed with release 1.5.0. Keep to verify correct functionality.
const isAditionDebug = (): boolean => {
  try {
    return localStorage.getItem('aditionDebug') === '1';
  } catch {
    return false;
  }
};

const logAdition = (...args: unknown[]): void => {
  if (!isAditionDebug()) return;
  console.log('[Adition]', ...args);
};

/**
 * get styles for inside iframe
 * @param height
 * @param type lp | plp
 * @param breakpoint small | medium (includes alo every breakpoint above)
 * @returns styles to add inside iframe
 */
const getStyles = ({ height = 0, type, breakpoint }: { height?: number; type: 'lp' | 'plp'; breakpoint: string }): string => {
    const styles: Record<'lp' | 'plp', Record<string, string>> = {
        lp: {
            small: `img {
                    width: 100%;
                    height: auto;
                    aspect-ratio: 6 / 5;
                }`,
            medium: `img {
                    width: 100%;
                    height: auto;
                    aspect-ratio: 3 / 1;
                }`
        },
        plp: {
            small: `body {
                    display: flex;
                    align-items: center;
                    height: ${height}px;
                }

                a {
                    height: auto;
                }

                img {
                    width: 100%;
                    height: auto;
                    aspect-ratio: 6 / 5;
                }`,
            xxlarge: `body {
                    text-align: center;
                    height: ${height}px;
                }

                a {
                    height: ${height}px;
                }

                img {
                    width: auto;
                    height: 100%;
                    aspect-ratio: 3 / 2;
                }`,
        }
    };

    return styles[type][breakpoint];
};

/**
 * get id depending on breakpoint
 */
const getId = (dataset: DOMStringMap, pageType: string | null): number => {
  const breakpoint = window.breakpoint();

  if (pageType === 'Search-Show') {
    return parseInt((breakpoint === 'xxlarge' ? dataset.idAditionXxlarge : dataset.idAditionXlargedown) ?? '');
  }

  return parseInt((breakpoint === 'small' ? dataset.idAditionSmall : dataset.idAditionMediumup) ?? '');
};

/**
 * hide or show ads in terms of using space
 * @param slots elements to show/hide
 * @param hide true hides, false shows
 */
const hideOrShow = (slots: Element | Element[] | NodeListOf<Element>, hide: boolean): void => {
  const classHide = 'cc-hidden';

  const resolveTarget = (slot: Element): Element | null =>
    document.querySelector('.js-page-plp') ? slot.closest('.cc-plp__item') : slot;

  const hasIframe = (el: Element): boolean => Boolean(el.querySelector('iframe'));

  const apply = (slot: Element): void => {
    const target = resolveTarget(slot);
    if (!target) return;

    if (hide && hasIframe(target)) return;

    target.classList.toggle(classHide, hide);
  };

  if (slots instanceof NodeList || Array.isArray(slots)) {
    slots.forEach(apply);
  } else {
    apply(slots);
  }
};

const insertStyles = (doc: Document, styles: string): void => {
    if (!doc.querySelector('.styles--sps')) doc.body.insertAdjacentHTML('afterbegin', `<style class="styles--sps">${styles}</style>`);
};

/**
 * make content fit to to container
 */
const fitSizeToContainer = ({ iframe, slot }: { iframe: HTMLIFrameElement; slot: HTMLElement }): void => {
    const doc = iframe.contentDocument!;
    const img = doc.querySelector('img');

    if (!(img && img.width && img.height)) {
        hideOrShow([slot], true);
        return;
    }

    if (document.querySelector('.js-page-plp')) {
        /* PLP */
        const heightCalculated = document.querySelector('.cc-plp__item')!.clientHeight;

        if (heightCalculated) iframe.style.height = `${heightCalculated}px`;

        insertStyles(doc, getStyles({
            height: heightCalculated,
            type: 'plp',
            breakpoint: window.breakpoint() === 'xxlarge' ? 'xxlarge' : 'small'
        }));
    } else {
        /* HOME */
        const heightCalculated = widthContainer * (window.breakpoint() === 'small' ? size.sm : size.md);

        if (heightCalculated) iframe.style.height = `${heightCalculated}px`;

        insertStyles(doc, getStyles({
            type: 'lp',
            breakpoint: window.breakpoint() === 'small' ? 'small' : 'medium'
        }));
    }
};

class Adserver {
    adserver: NodeListOf<HTMLElement>;
    pageFilter: { filterName: string; filterValue: string }[] = [];
    pageType: string | null = null;

    constructor() {
        this.adserver = document.querySelectorAll<HTMLElement>(SELECTORS.adserver);

        if (!this.adserver.length) {
            return;
        }

        const pageFilterNode = document.querySelector<HTMLElement>(SELECTORS.dataPageFilter);
        this.pageFilter = pageFilterNode ? JSON.parse(pageFilterNode.dataset.pageFilter!) : [];
        this.pageType = document.querySelector(SELECTORS.page)!.getAttribute('data-action');
        this.init();
        this.loadAdserver();
    }

    init(): void {
        window.adition = window.adition || { srq: [] };
        window.adition.srq = window.adition.srq || [];
    }

    loadAdserver(): void {
        const pageFilter = this.pageFilter;
        const slots = this.adserver;
        const pageType = this.pageType;
        const noBannerSlots = new Set<HTMLElement>();
        const slotsWithContent = new Set<HTMLElement>();

        if (slots.length) {
            // always read srq from window: once loaded, Adition replaces the initial array with its own queue
            window.adition!.srq.push(api => {
                api.registerAdfarm(`${window.spsDataLayer.endpoints!.adserverEndpoint}`);

                pageFilter.forEach(({ filterName, filterValue }) => {
                    api.setProfile(filterName, filterValue);
                });

                slots.forEach(slot => {
                    api.configureRenderSlot(slot.id).setContentunitId(getId(slot.dataset, pageType));
                });

                api.events.onNoBanner(slotEl => {
                    // TODO, remove. Keep for one release to verify correct functionality
                    logAdition('onNoBanner', slotEl.id);
                    noBannerSlots.add(slotEl);
                });

                api.events.onFinishLoading((slotEl, apiRef, iframeEl) => {
                    // TODO, remove. Keep for one release to verify correct functionality
                    logAdition('onFinishLoading', slotEl.id);
                    const iframe = iframeEl || slotEl.querySelector('iframe');
                    slotsWithContent.add(slotEl);
                    if (iframe) fitSizeToContainer({ iframe, slot: slotEl });
                });

                api.events.onComplete(() => {
                    // TODO, remove. Keep for one release to verify correct functionality
                    noBannerSlots.forEach(slotEl => {
                        if (!slotsWithContent.has(slotEl)) {
                            logAdition('onComplete hide slot', slotEl);
                            hideOrShow(slotEl, true);
                        }
                    });

                    noBannerSlots.clear();
                    slotsWithContent.clear();
                });

                api.load().completeRendering();

                slots.forEach(slot => {
                    window.adition!.srq.push(ad => {
                        ad.renderSlot(slot.id);
                    });
                });
            });
        }
    }
}

const init = (): Adserver => new Adserver();

/**
 * Consent-controlled bootstrap for Adition:
 * initializes the ad server if consent is granted,
 * otherwise hides all Adition slots.
 */
const handleAditionConsent = async (): Promise<void> => {
    const UC = window.UC_UI;
    if (!UC?.getServicesBaseInfo) return;

    const services = await UC.getServicesBaseInfo();
    if (!Array.isArray(services)) return;

    const adition = services.find(s => s.name === 'Adition');
    const hasConsent = adition?.consent?.status === true;

    if (!hasConsent) return;

    init();
};

const hideAditionSlotsFallback = (): void => {
  document.querySelectorAll(SELECTORS.adserver).forEach(slot => hideOrShow(slot, true));
};

window.addEventListener('UC_UI_INITIALIZED', async () => {
  try {
    logAdition('UC_UI_INITIALIZED');

    const UC = window.UC_UI;
    if (!UC?.getServicesBaseInfo) {
      hideAditionSlotsFallback();
      return;
    }

    const services = await UC.getServicesBaseInfo();
    if (!Array.isArray(services)) {
      hideAditionSlotsFallback();
      return;
    }

    const adition = services.find(s => s.name === 'Adition');
    const hasConsent = adition?.consent?.status === true;

    if (!hasConsent) {
      logAdition('no consent → fallback hide');
      hideAditionSlotsFallback();
    }
  } catch (e) {
    logAdition('UC error → fallback hide', e);
    hideAditionSlotsFallback();
  }
});

window.spsDataLayer = window.spsDataLayer || {};
window.spsDataLayer.internal = window.spsDataLayer.internal || {};
window.spsDataLayer.internal.handleAditionConsent = handleAditionConsent;

/* it is necessary to make it globally available because of usercentrics activating the script */
window.spsDataLayer.init!.adserver = init;

export {};
